import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import Pagination from '../../../../shared/components/Pagination';
import {
  ArrowLeft,
  Shield,
  Building,
  Flame,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Award,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { DistributionPieChart, StatCard } from '../../components/analytics';
import {
  useGetSamagamSafetyTotalDetailsQuery,
  useGetSamagamSafetyCertificateDetailsQuery,
} from '../../services/kvsSamagamApi';
import { SafetySchoolRow, SafetySummary } from '../../services/api';

// ─── Mock data (fallback when API unavailable) ────────────────────────────────
const MOCK_SCHOOLS: SafetySchoolRow[] = [
  { Sn: 1,  School: { ID: 101, Name: 'KV No.1 AFS Agra' },        State: 'Uttar Pradesh', RO: { ID: 1, Name: 'Agra Region' },    BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'PWD',               ValidFrom: '2023-04-01', ValidTo: '2026-03-31' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'Fire Dept',        ValidFrom: '2024-01-01', ValidTo: '2025-12-31' } },
  { Sn: 2,  School: { ID: 102, Name: 'KV No.2 AFS Agra' },        State: 'Uttar Pradesh', RO: { ID: 1, Name: 'Agra Region' },    BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'PWD',               ValidFrom: '2022-06-01', ValidTo: '2025-05-31' }, FireSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,               ValidFrom: null,         ValidTo: null          } },
  { Sn: 3,  School: { ID: 103, Name: 'KV Mathura Cantt' },        State: 'Uttar Pradesh', RO: { ID: 1, Name: 'Agra Region' },    BuildingSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,              ValidFrom: null,         ValidTo: null          }, FireSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,               ValidFrom: null,         ValidTo: null          } },
  { Sn: 4,  School: { ID: 201, Name: 'KV No.1 Chennai' },         State: 'Tamil Nadu',    RO: { ID: 2, Name: 'Chennai Region' }, BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'CPWD',              ValidFrom: '2023-01-15', ValidTo: '2024-12-15' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'TN Fire & Rescue', ValidFrom: '2023-07-01', ValidTo: '2025-06-30' } },
  { Sn: 5,  School: { ID: 202, Name: 'KV NAD Karanja' },          State: 'Tamil Nadu',    RO: { ID: 2, Name: 'Chennai Region' }, BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'CPWD',              ValidFrom: '2021-10-01', ValidTo: '2024-09-30' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'TN Fire & Rescue', ValidFrom: '2022-04-01', ValidTo: '2025-03-31' } },
  { Sn: 6,  School: { ID: 301, Name: 'KV Paharganj Delhi' },      State: 'Delhi',         RO: { ID: 3, Name: 'Delhi Region' },   BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'MCD',               ValidFrom: '2024-01-01', ValidTo: '2027-12-31' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'Delhi Fire Service', ValidFrom: '2024-03-01', ValidTo: '2025-02-28' } },
  { Sn: 7,  School: { ID: 302, Name: 'KV Andrews Ganj' },         State: 'Delhi',         RO: { ID: 3, Name: 'Delhi Region' },   BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'MCD',               ValidFrom: '2022-07-01', ValidTo: '2025-06-30' }, FireSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,               ValidFrom: null,         ValidTo: null          } },
  { Sn: 8,  School: { ID: 401, Name: 'KV Bhubaneswar No.1' },     State: 'Odisha',        RO: { ID: 4, Name: 'Bhubaneswar RO' }, BuildingSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,              ValidFrom: null,         ValidTo: null          }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'Odisha Fire Service', ValidFrom: '2023-10-01', ValidTo: '2026-09-30' } },
  { Sn: 9,  School: { ID: 501, Name: 'KV No.1 Chandigarh' },      State: 'Chandigarh',    RO: { ID: 5, Name: 'Chandigarh RO' },  BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'CPWD',              ValidFrom: '2020-09-01', ValidTo: '2023-08-31' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'Chandigarh Fire',   ValidFrom: '2024-02-01', ValidTo: '2026-01-31' } },
  { Sn: 10, School: { ID: 502, Name: 'KV Sector 30 Chandigarh' }, State: 'Chandigarh',    RO: { ID: 5, Name: 'Chandigarh RO' },  BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'PWD',               ValidFrom: '2023-05-01', ValidTo: '2026-04-30' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'Chandigarh Fire',   ValidFrom: '2023-11-01', ValidTo: '2025-10-31' } },
  { Sn: 11, School: { ID: 601, Name: 'KV Kalaikunda' },           State: 'West Bengal',   RO: { ID: 6, Name: 'Kolkata Region' }, BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'PWD',               ValidFrom: '2022-03-01', ValidTo: '2025-02-28' }, FireSafety: { CertificateAvailable: 'NO',  IssuingAgency: null,               ValidFrom: null,         ValidTo: null          } },
  { Sn: 12, School: { ID: 602, Name: 'KV Fort William' },         State: 'West Bengal',   RO: { ID: 6, Name: 'Kolkata Region' }, BuildingSafety: { CertificateAvailable: 'YES', IssuingAgency: 'CPWD',              ValidFrom: '2024-06-01', ValidTo: '2027-05-31' }, FireSafety: { CertificateAvailable: 'YES', IssuingAgency: 'WB Fire Service',   ValidFrom: '2024-04-01', ValidTo: '2027-03-31' } },
];
const MOCK_SUMMARY: SafetySummary = {
  TotalSchools: 12,
  BuildingCertified: 9,
  FireCertified: 8,
  BothCertified: 7,
  NeitherCertified: 2,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseDate = (s: string | null): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
type ValidityStatus = 'valid' | 'expiring' | 'expired' | 'na';
const validityStatus = (validTo: string | null): ValidityStatus => {
  const d = parseDate(validTo);
  if (!d) return 'na';
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  if (diff < 0) return 'expired';
  if (diff < 90 * 24 * 60 * 60 * 1000) return 'expiring';
  return 'valid';
};
const STATUS_META: Record<ValidityStatus, { label: string; color: string; bg: string; Icon: React.ElementType }> = {
  valid:    { label: 'Valid',         color: 'text-green-700',  bg: 'bg-green-100',  Icon: CheckCircle2  },
  expiring: { label: 'Expiring Soon', color: 'text-yellow-700', bg: 'bg-yellow-100', Icon: Clock         },
  expired:  { label: 'Expired',       color: 'text-red-700',    bg: 'bg-red-100',    Icon: AlertTriangle },
  na:       { label: 'N/A',           color: 'text-gray-500',   bg: 'bg-gray-100',   Icon: XCircle       },
};
const fmtDate = (s: string | null) => {
  const d = parseDate(s);
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Component ────────────────────────────────────────────────────────────────
const SafetyAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'building' | 'fire'>('building');
  const [search, setSearch] = useState('');

  // Fetch data using RTK Query hooks
  const { 
    data: totalDetails, 
    isLoading: totalLoading, 
    refetch: refetchTotal 
  } = useGetSamagamSafetyTotalDetailsQuery();
  
  const { 
    data: detailsData, 
    isLoading: detailsLoading, 
    refetch: refetchDetails 
  } = useGetSamagamSafetyCertificateDetailsQuery();

  const loading = totalLoading || detailsLoading;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadData = () => {
    refetchTotal();
    refetchDetails();
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [search, activeTab]);

  // Map API response to legacy SafetySchoolRow interface for compatibility
  const schools = useMemo(() => {
    if (!detailsData || !detailsData.schools) return MOCK_SCHOOLS;
    return detailsData.schools.map((s, idx) => ({
      Sn: idx + 1,
      School: { ID: s.schoolID, Name: s.schoolName },
      State: s.stateName || '',
      RO: { ID: 0, Name: s.roName || '' },
      BuildingSafety: {
        CertificateAvailable: (s.buildingSafety?.toLowerCase() === 'available' || s.buildingSafetyCertificate?.toLowerCase() === 'available') ? 'YES' : 'NO' as 'YES' | 'NO',
        IssuingAgency: s.agency && s.agency !== 'N/A' && s.agency !== 'N.A.' ? s.agency : null,
        ValidFrom: s.buildingValidFrom && s.buildingValidFrom !== 'N/A' ? s.buildingValidFrom : (s.validFrom && s.validFrom !== 'N/A' ? s.validFrom : null),
        ValidTo: s.buildingValidTo && s.buildingValidTo !== 'N/A' ? s.buildingValidTo : (s.validTo && s.validTo !== 'N/A' ? s.validTo : null),
      },
      FireSafety: {
        CertificateAvailable: (s.fireSafety?.toLowerCase() === 'available' || s.fireSafetyCertificate?.toLowerCase() === 'available') ? 'YES' : 'NO' as 'YES' | 'NO',
        IssuingAgency: s.fireAgencyOther && s.fireAgencyOther !== 'N/A' && s.fireAgencyOther !== 'N.A.' ? s.fireAgencyOther : null,
        ValidFrom: s.fireValidFrom && s.fireValidFrom !== 'N/A' ? s.fireValidFrom : null,
        ValidTo: s.fireValidTo && s.fireValidTo !== 'N/A' ? s.fireValidTo : null,
      }
    }));
  }, [detailsData]);

  const summary = useMemo(() => {
    if (!detailsData || !detailsData.schools) return MOCK_SUMMARY;
    const totalSchools = detailsData.summary?.totalSchools || schools.length;
    const buildingCertified = schools.filter(s => s.BuildingSafety.CertificateAvailable === 'YES').length;
    const fireCertified = schools.filter(s => s.FireSafety.CertificateAvailable === 'YES').length;
    const bothCertified = schools.filter(
      s => s.BuildingSafety.CertificateAvailable === 'YES' && s.FireSafety.CertificateAvailable === 'YES'
    ).length;
    const neitherCertified = schools.filter(
      s => s.BuildingSafety.CertificateAvailable === 'NO' && s.FireSafety.CertificateAvailable === 'NO'
    ).length;

    return {
      TotalSchools: totalSchools,
      BuildingCertified: buildingCertified,
      FireCertified: fireCertified,
      BothCertified: bothCertified,
      NeitherCertified: neitherCertified,
    };
  }, [schools, detailsData]);

  const certKey = activeTab === 'building' ? 'BuildingSafety' : 'FireSafety';
  const tabLabel = activeTab === 'building' ? 'Building Safety' : 'Fire Safety';

  const certified   = schools.filter(s => s[certKey].CertificateAvailable === 'YES');
  const uncertified = schools.filter(s => s[certKey].CertificateAvailable === 'NO');

  const validityCount = { valid: 0, expiring: 0, expired: 0, na: 0 };
  certified.forEach(s => validityCount[validityStatus(s[certKey].ValidTo)]++);

  const agencies = useMemo(() => {
    const categoryMatch = activeTab === 'building' ? 'building' : 'fire';
    const catData = totalDetails?.find(c => 
      c.Category.toLowerCase().includes(categoryMatch)
    );
    
    if (catData && catData.Breakdown && catData.Breakdown.length > 0) {
      const sortedBreakdown = [...catData.Breakdown].sort((a: any, b: any) => (b.Total || b.total || 0) - (a.Total || a.total || 0));
      const totalCount = catData.Breakdown.reduce((sum: number, item: any) => sum + (item.Total || item.total || 0), 0);
      
      return sortedBreakdown.map((item: any) => {
        const name = item.Agency || item.agency || 'Unknown';
        const count = item.Total || item.total || 0;
        return {
          name,
          count,
          pct: Math.round((count / (totalCount || 1)) * 100),
        };
      });
    }

    // Fallback: calculate from schools list
    const agencyMap: Record<string, number> = {};
    certified.forEach(s => {
      const ag = s[certKey].IssuingAgency || 'Unknown';
      agencyMap[ag] = (agencyMap[ag] || 0) + 1;
    });
    return Object.entries(agencyMap)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ 
        name, 
        count, 
        pct: Math.round((count / (certified.length || 1)) * 100) 
      }));
  }, [totalDetails, activeTab, certified, certKey]);

  const availPie = [
    { name: 'Certificate Available', value: certified.length,   color: '#10b981' },
    { name: 'Not Available',         value: uncertified.length, color: '#ef4444' },
  ];
  const agencyPie = agencies.slice(0, 6).map((a, i) => ({
    name:  a.name,
    value: a.count,
    color: ['#6366f1', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6'][i],
  }));

  const filtered = useMemo(() => schools.filter(s =>
    !search ||
    s.School.Name.toLowerCase().includes(search.toLowerCase()) ||
    s.State.toLowerCase().includes(search.toLowerCase()) ||
    s.RO.Name.toLowerCase().includes(search.toLowerCase())
  ), [schools, search]);

  const totalRecords = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedSchools = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filtered.slice(startIndex, startIndex + pageSize);
  }, [filtered, currentPage, pageSize]);

  const exportToCSV = () => {
    const headers = [
      'Sn',
      'School Name',
      'State',
      'RO Name',
      'Certificate Available',
      'Issuing Agency',
      'Valid From',
      'Valid To',
      'Status',
    ];

    const rows = filtered.map(s => {
      const cert = s[certKey];
      const status = STATUS_META[validityStatus(cert.ValidTo)].label;
      return [
        s.Sn,
        s.School.Name,
        s.State,
        s.RO.Name,
        cert.CertificateAvailable,
        cert.IssuingAgency || '—',
        fmtDate(cert.ValidFrom),
        fmtDate(cert.ValidTo),
        status,
      ];
    });

    const escapeValue = (value: any) => {
      const str = String(value ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csv = [headers, ...rows].map(row => row.map(escapeValue).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `safety_analysis_${activeTab}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Analytics', path: '/analytics' },
    { label: 'Safety Analysis' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-6">
        <Breadcrumb />

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <Shield className="h-7 w-7 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Safety Analysis</h1>
              <p className="text-sm text-gray-500">Building Safety &amp; Fire Safety Certificates</p>
            </div>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-10 w-10 text-purple-600 animate-spin" />
          </div>
        )}

        {!loading && (
          <>
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <StatCard title="Total Schools"      value={summary.TotalSchools}      icon={<Award className="h-5 w-5 text-purple-600" />}      color="purple" />
                <StatCard title="Building Certified" value={summary.BuildingCertified} icon={<Building className="h-5 w-5 text-blue-600" />}     color="blue"   />
                <StatCard title="Fire Certified"     value={summary.FireCertified}     icon={<Flame className="h-5 w-5 text-orange-600" />}      color="orange" />
                <StatCard title="Both Certified"     value={summary.BothCertified}     icon={<CheckCircle2 className="h-5 w-5 text-green-600" />} color="green"  />
                <StatCard title="Neither Certified"  value={summary.NeitherCertified}  icon={<XCircle className="h-5 w-5 text-red-600" />}       color="red"    />
              </div>
            )}

            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('building')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'building'
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Building className="h-4 w-4" /> Building Safety
              </button>
              <button
                onClick={() => setActiveTab('fire')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'fire'
                    ? 'bg-orange-500 text-white shadow'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Flame className="h-4 w-4" /> Fire Safety
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">{tabLabel} — Certificate Availability</h3>
                <DistributionPieChart data={availPie} title={`${tabLabel} — Certificate Availability`} />
                <div className="flex justify-center gap-6 mt-3 text-sm">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Available ({certified.length})
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Not Available ({uncertified.length})
                  </span>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">{tabLabel} — Issuing Agencies</h3>
                {agencyPie.length > 0 ? (
                  <DistributionPieChart data={agencyPie} title={`${tabLabel} — Issuing Agencies`} />
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No certificate data</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {(['valid', 'expiring', 'expired', 'na'] as ValidityStatus[]).map(st => {
                const m = STATUS_META[st];
                const Icon = m.Icon;
                return (
                  <div key={st} className={`${m.bg} rounded-xl p-4 flex items-center gap-3`}>
                    <Icon className={`h-6 w-6 ${m.color}`} />
                    <div>
                      <p className={`text-xs font-medium ${m.color}`}>{m.label}</p>
                      <p className={`text-2xl font-bold ${m.color}`}>{validityCount[st]}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/*
            {agencies.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 mb-6">
                <h3 className="font-semibold text-gray-700 mb-4">{tabLabel} — Certificates by Issuing Agency</h3>
                <div className="space-y-3">
                  {agencies.map(ag => (
                    <div key={ag.name} className="flex items-center gap-3">
                      <span className="w-44 text-sm text-gray-600 truncate" title={ag.name}>{ag.name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div className="h-4 rounded-full bg-purple-500" style={{ width: ag.pct + '%' }} />
                      </div>
                      <span className="w-12 text-sm font-semibold text-gray-700 text-right">{ag.count}</span>
                      <span className="w-10 text-xs text-gray-400 text-right">{ag.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            */}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-purple-600 px-5 py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="font-semibold text-white">{tabLabel} — School-wise Certificate Details</h3>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    placeholder="Search school / state / RO..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="text-sm px-3 py-1.5 rounded-lg border-0 bg-white/20 text-white placeholder-white/70 focus:outline-none focus:bg-white/30 w-56"
                  />
                  <select
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value))}
                    className="text-sm rounded-lg border border-white/20 bg-white/20 px-3 py-1.5 text-purple-700 placeholder-white/70 focus:outline-none focus:bg-white/30"
                  >
                    {[10, 20, 50].map(size => (
                      <option key={size} value={size}>{size} rows</option>
                    ))}
                  </select>
                  <button
                    onClick={exportToCSV}
                    disabled={filtered.length === 0}
                    className="flex items-center justify-center rounded-lg bg-white text-purple-700 px-4 py-2 text-sm font-medium hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">Sn</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">School Name</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">State</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">RO Name</th>
                      <th className="px-4 py-3 text-center text-purple-800 font-semibold">Certificate</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">Issuing Agency</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">Valid From</th>
                      <th className="px-4 py-3 text-left text-purple-800 font-semibold">Valid To</th>
                      <th className="px-4 py-3 text-center text-purple-800 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSchools.map((s, idx) => {
                      const cert = s[certKey];
                      const st   = validityStatus(cert.ValidTo);
                      const meta = STATUS_META[st];
                      const Icon = meta.Icon;
                      return (
                        <tr key={s.Sn} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-gray-500">{s.Sn}</td>
                          <td className="px-4 py-3 font-medium text-gray-800">{s.School.Name}</td>
                          <td className="px-4 py-3 text-gray-600">{s.State}</td>
                          <td className="px-4 py-3 text-gray-600">{s.RO.Name}</td>
                          <td className="px-4 py-3 text-center">
                            {cert.CertificateAvailable === 'YES' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                <CheckCircle2 className="h-3 w-3" /> YES
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                <XCircle className="h-3 w-3" /> NO
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{cert.IssuingAgency || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{fmtDate(cert.ValidFrom)}</td>
                          <td className="px-4 py-3 text-gray-600">{fmtDate(cert.ValidTo)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} text-xs font-medium`}>
                              <Icon className="h-3 w-3" /> {meta.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-4 py-10 text-center text-gray-400">No schools match your search.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isLoading={loading}
              />
            </div>
        </>
      )}

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'Safety';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SafetyAnalysis;
